import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';

interface Org {
  id: string;
  name: string;
}

interface OrgPickerPanelProps {
  orgs: Org[];
  loading: boolean;
  isSubmitting: boolean;
  onSubmit: (orgIds: string[], force: boolean) => void;
}

export function OrgPickerPanel({ orgs, loading, isSubmitting, onSubmit }: OrgPickerPanelProps) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [force, setForce] = useState(false);

  const filtered = orgs.filter(o =>
    o.name.toLowerCase().includes(search.toLowerCase()),
  );

  const toggleOrg = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(filtered.map(o => o.id)));
  const clearAll = () => setSelected(new Set());

  const overLimit = selected.size > 50;
  const canSubmit = selected.size > 0 && !overLimit && !isSubmitting;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Select Organisations</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Input
          placeholder="Search organisations…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="flex gap-2">
          <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={selectAll}>
            Select all
          </Button>
          <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={clearAll}>
            Clear
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Loading organisations…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-gray-500">No organisations found.</p>
        ) : (
          <ScrollArea className="h-72 border rounded-md p-2">
            {filtered.map(org => (
              <div key={org.id} className="flex items-center gap-2 py-1">
                <Checkbox
                  id={`org-${org.id}`}
                  checked={selected.has(org.id)}
                  onCheckedChange={() => toggleOrg(org.id)}
                />
                <Label htmlFor={`org-${org.id}`} className="flex-1 cursor-pointer text-sm">
                  {org.name}
                  <span className="ml-2 text-xs text-gray-400 font-mono">
                    {org.id.slice(0, 8)}…
                  </span>
                </Label>
              </div>
            ))}
          </ScrollArea>
        )}

        <div className="flex items-center gap-2">
          <Checkbox
            id="force-toggle"
            checked={force}
            onCheckedChange={v => setForce(Boolean(v))}
          />
          <Label htmlFor="force-toggle" className="cursor-pointer text-sm">
            Force re-migrate orgs already on staging
          </Label>
        </div>

        {overLimit && (
          <p className="text-sm text-red-600">
            Max 50 orgs per run — {selected.size} selected.
          </p>
        )}

        <Button
          onClick={() => { if (canSubmit) onSubmit(Array.from(selected), force); }}
          disabled={!canSubmit}
          className="w-full"
        >
          {isSubmitting ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Starting…</>
          ) : overLimit ? (
            'Max 50 orgs per run'
          ) : (
            'Start migration'
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
